/**
 * Generate branded PDF factsheets for single components or a full-scope
 * pack (cover page + one page per selected component). Purely client-side
 * (uses jsPDF), so it works wherever the estimator state is available.
 */
import { jsPDF } from 'jspdf';
import { COMPONENT_INFO, ROLE_LABELS } from '../pages/estimator/constants';
import { getGroupLabel } from './groupLabels';

const BRAND_NAVY = [15, 32, 64];
const BRAND_TEAL = [74, 191, 207];
const SLATE_500 = [100, 116, 139];
const SLATE_700 = [51, 65, 85];
const SLATE_400 = [148, 163, 184];
const SLATE_200 = [226, 232, 240];
const AMBER_BG = [254, 243, 199];
const AMBER_FG = [180, 83, 9];

function detectImageFormat(dataUri) {
  // Returns a jsPDF-compatible format hint from a data URI, or null for SVG/unknown.
  if (!dataUri || typeof dataUri !== 'string') return null;
  const m = dataUri.match(/^data:image\/([a-zA-Z0-9+.-]+);/);
  if (!m) return 'PNG';
  const fmt = m[1].toLowerCase();
  if (fmt === 'jpeg' || fmt === 'jpg') return 'JPEG';
  if (fmt === 'png') return 'PNG';
  if (fmt === 'webp') return 'WEBP';
  return null; // svg+xml, gif — unsupported by jsPDF.addImage
}

function drawHeader(doc, label = 'SEERTECH \u2014 COMPONENT FACTSHEET', clientLogoDataUri = null) {
  const w = doc.internal.pageSize.getWidth();
  doc.setFillColor(...BRAND_NAVY); doc.rect(0, 0, w, 32, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold'); doc.setFontSize(10);
  doc.text(label, 54, 20);
  const fmt = detectImageFormat(clientLogoDataUri);
  if (clientLogoDataUri && fmt) {
    try {
      // Small logo tucked inside the header bar, right-aligned.
      doc.addImage(clientLogoDataUri, fmt, w - 54 - 60, 6, 60, 20, undefined, 'FAST');
    } catch { /* ignore */ }
  }
}

function drawFooter(doc, feedbackUrl = null) {
  const w = doc.internal.pageSize.getWidth();
  const h = doc.internal.pageSize.getHeight();
  doc.setFont('helvetica', 'normal'); doc.setFontSize(8);
  doc.setTextColor(...SLATE_400);
  doc.text(`Generated ${new Date().toLocaleDateString()}  \u2022  Seertech Blueprint Studio`, w / 2, h - 24, { align: 'center' });
  if (feedbackUrl) {
    // Clickable "Share feedback" link centered above the footer line.
    doc.setFont('helvetica', 'bold'); doc.setFontSize(9);
    doc.setTextColor(74, 191, 207); // BRAND_TEAL
    const linkText = '\u2192 Share feedback on this scope';
    const lw = doc.getTextWidth(linkText);
    const lx = (w - lw) / 2;
    const ly = h - 38;
    doc.textWithLink(linkText, lx, ly, { url: feedbackUrl });
  }
}

function renderComponentPage(doc, component, startY = 60) {
  const margin = 54;
  const pageW = doc.internal.pageSize.getWidth();
  const contentW = pageW - margin * 2;
  let y = startY;

  const info = COMPONENT_INFO[component.name] || {};
  const rationale = component.required
    ? (component.required_reason || info.hours_rationale || 'Mandatory component for every Seertech LMS implementation \u2014 cannot be removed from scope.')
    : component.recommended
      ? (component.recommended_reason || info.hours_rationale || 'Recommended default for most implementations. Can be de-scoped.')
      : info.hours_rationale;
  const description = info.description
    || `${component.name} is a ${getGroupLabel(component.group, 'default')} component${component.role ? ` delivered by ${ROLE_LABELS[component.role] || component.role}` : ''}.`;
  const hours = component.base_hours || {};

  // Title
  doc.setTextColor(...BRAND_NAVY); doc.setFontSize(20); doc.setFont('helvetica', 'bold');
  doc.text(component.name, margin, y);
  y += 20;

  // Scope badge
  if (component.required) {
    doc.setFillColor(...BRAND_NAVY);
    doc.roundedRect(margin, y - 10, 60, 14, 3, 3, 'F');
    doc.setTextColor(255, 255, 255); doc.setFontSize(8); doc.setFont('helvetica', 'bold');
    doc.text('REQUIRED', margin + 30, y, { align: 'center' });
  } else if (component.recommended) {
    doc.setFillColor(...AMBER_BG);
    doc.roundedRect(margin, y - 10, 80, 14, 3, 3, 'F');
    doc.setTextColor(...AMBER_FG); doc.setFontSize(8); doc.setFont('helvetica', 'bold');
    doc.text('RECOMMENDED', margin + 40, y, { align: 'center' });
  }
  y += 22;

  // Meta
  doc.setTextColor(...SLATE_500); doc.setFontSize(10); doc.setFont('helvetica', 'normal');
  const meta = `${getGroupLabel(component.group, 'default')}${component.role ? `   \u2022   ${ROLE_LABELS[component.role] || component.role}` : ''}`;
  doc.text(meta, margin, y);
  y += 20;

  const section = (title) => {
    doc.setDrawColor(...SLATE_200); doc.line(margin, y, pageW - margin, y);
    y += 14;
    doc.setTextColor(...BRAND_NAVY); doc.setFontSize(9); doc.setFont('helvetica', 'bold');
    doc.text(title.toUpperCase(), margin, y);
    y += 12;
    doc.setTextColor(...SLATE_700); doc.setFontSize(11); doc.setFont('helvetica', 'normal');
  };

  section('About');
  const descLines = doc.splitTextToSize(description, contentW);
  doc.text(descLines, margin, y);
  y += descLines.length * 14 + 10;

  if (rationale) {
    section(component.required ? 'Why it is required' : 'Why it is recommended');
    const ratLines = doc.splitTextToSize(rationale, contentW);
    doc.text(ratLines, margin, y);
    y += ratLines.length * 14 + 10;
  }

  if (hours.simple !== undefined || hours.standard !== undefined || hours.complex !== undefined) {
    section('Base effort by complexity');
    const cellW = contentW / 3;
    ['simple', 'standard', 'complex'].forEach((level, i) => {
      const x = margin + i * cellW;
      doc.setDrawColor(...SLATE_200);
      doc.roundedRect(x + 4, y, cellW - 8, 50, 4, 4);
      doc.setTextColor(...SLATE_500); doc.setFontSize(8); doc.setFont('helvetica', 'bold');
      doc.text(level.toUpperCase(), x + cellW / 2, y + 14, { align: 'center' });
      doc.setTextColor(...BRAND_NAVY); doc.setFontSize(18);
      doc.text(String(hours[level] ?? 0), x + cellW / 2, y + 34, { align: 'center' });
      doc.setFontSize(8); doc.setTextColor(...SLATE_500); doc.setFont('helvetica', 'normal');
      doc.text('hrs', x + cellW / 2, y + 44, { align: 'center' });
    });
    y += 64;
    doc.setFont('helvetica', 'italic'); doc.setFontSize(9); doc.setTextColor(...SLATE_400);
    doc.text('Actual effort is modified by project factors, implementation type, and data-integrity uplift.', margin, y);
    y += 14;
  }
}

/**
 * Export a single component as a branded factsheet PDF.
 */
export function exportComponentFactsheet(component) {
  const doc = new jsPDF({ unit: 'pt', format: 'letter' });
  doc.setProperties({
    title: `${component.name} — Factsheet`,
    subject: `${component.name} component factsheet`,
    author: 'Seertech Blueprint Studio',
    creator: 'Seertech Blueprint Studio',
    keywords: ['Seertech', 'LMS', 'Factsheet', component.name, component.group || ''].filter(Boolean).join(', '),
  });
  drawHeader(doc);
  renderComponentPage(doc, component, 60);
  drawFooter(doc);
  const filename = `${component.name.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}-factsheet.pdf`;
  doc.save(filename);
  return filename;
}

/**
 * Export a full Scope Pack: cover page + one page per selected component.
 * @param {object} estimate — { organizationName, implementationName, implementationType, complexity, totalEffort, totalCost, preparedBy }
 * @param {Array<object>} components — selected component dicts (each like /api/components entry)
 * @param {object} [options]
 *   - onlyRequired:       boolean — exclude optional (non-required, non-recommended) components
 *   - includeCoverStats:  boolean — show Total Effort / Total Cost on cover (default true)
 *   - includePreparedBy:  boolean — show "Prepared by <name> <email>" on cover (default true)
 *   - customNote:         string  — free-text note rendered on the cover
 *   - clientLogoDataUri:  string  — data URI for the client logo (placed on cover, top-right)
 */
/**
 * Build (but do not save) a Scope Pack jsPDF doc. Returns { doc, filename, pageCount }
 * so callers can either `.save()` locally, transmit base64 to the backend, etc.
 */
export function buildScopePack(estimate, components, options = {}) {
  const {
    onlyRequired = false,
    includeCoverStats = true,
    includePreparedBy = true,
    customNote = '',
    clientLogoDataUri = null,
    feedbackUrl = null,
  } = options;

  const filteredComponents = onlyRequired
    ? components.filter(c => c.required || c.recommended)
    : components;

  const doc = new jsPDF({ unit: 'pt', format: 'letter' });
  // PDF metadata — shows in viewer's tab/title bar, file info panels, Spotlight, etc.
  const preparedByMeta = estimate.preparedBy || {};
  const orgForMeta = estimate.organizationName || 'Client';
  const implForMeta = estimate.implementationName ? ` \u2014 ${estimate.implementationName}` : '';
  const authorLine = preparedByMeta.name
    ? (preparedByMeta.email ? `${preparedByMeta.name} <${preparedByMeta.email}>` : preparedByMeta.name)
    : 'Seertech Blueprint Studio';
  doc.setProperties({
    title: `${orgForMeta} Scope Pack${implForMeta}`,
    subject: `Seertech LMS implementation scope pack for ${orgForMeta}`,
    author: authorLine,
    creator: 'Seertech Blueprint Studio',
    keywords: ['Seertech', 'Scope Pack', 'LMS Implementation', orgForMeta, estimate.implementationType || '', estimate.complexity || '']
      .filter(Boolean).join(', '),
  });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 54;
  const contentW = pageW - margin * 2;

  // ── Cover page ────────────────────────────────────────────────
  // Cover uses a dedicated larger logo block below, so we skip the tiny
  // header-bar logo here to avoid duplication.
  drawHeader(doc, 'SEERTECH \u2014 SCOPE PACK');

  // Optional client logo (top-right under header) — larger display on cover
  const coverLogoFmt = detectImageFormat(clientLogoDataUri);
  if (clientLogoDataUri && coverLogoFmt) {
    try {
      const logoH = 48;
      const logoW = 120;
      doc.addImage(clientLogoDataUri, coverLogoFmt, pageW - margin - logoW, 44, logoW, logoH, undefined, 'FAST');
    } catch { /* silently ignore */ }
  }

  // Title block
  let y = 140;
  doc.setTextColor(...BRAND_NAVY); doc.setFontSize(28); doc.setFont('helvetica', 'bold');
  doc.text('Project Scope Pack', margin, y);
  y += 26;
  doc.setFontSize(14); doc.setTextColor(...SLATE_500); doc.setFont('helvetica', 'normal');
  doc.text(estimate.organizationName || 'Client Engagement', margin, y);
  y += 14;
  if (estimate.implementationName) {
    doc.setFontSize(11);
    doc.text(estimate.implementationName, margin, y);
    y += 16;
  }

  // Divider
  y += 10;
  doc.setDrawColor(...BRAND_TEAL); doc.setLineWidth(2);
  doc.line(margin, y, margin + 80, y);
  doc.setLineWidth(1);
  y += 26;

  // Prepared by (personal signature line) — optional
  const prep = estimate.preparedBy;
  if (includePreparedBy && prep && (prep.name || prep.email)) {
    doc.setTextColor(...SLATE_500); doc.setFontSize(8); doc.setFont('helvetica', 'bold');
    doc.text('PREPARED BY', margin, y);
    y += 12;
    doc.setTextColor(...BRAND_NAVY); doc.setFontSize(11); doc.setFont('helvetica', 'normal');
    doc.text(prep.name || prep.email, margin, y);
    if (prep.name && prep.email) {
      y += 14;
      doc.setTextColor(...SLATE_500); doc.setFontSize(9);
      doc.text(prep.email, margin, y);
    }
    y += 22;
  }

  // Summary stats grid (2x2) — optional
  if (includeCoverStats) {
    const stats = [
      ['Implementation Type', estimate.implementationType || '\u2014'],
      ['Complexity', estimate.complexity || '\u2014'],
      ['Total Effort', estimate.totalEffort ? `${Math.round(estimate.totalEffort).toLocaleString()} hrs` : '\u2014'],
      ['Total Cost', estimate.totalCost ? `${estimate.currencySymbol || '$'}${Math.round(estimate.totalCost).toLocaleString()}` : '\u2014'],
    ];
    const cellW = contentW / 2;
    stats.forEach(([label, value], i) => {
      const col = i % 2;
      const row = Math.floor(i / 2);
      const x = margin + col * cellW;
      const cy = y + row * 64;
      doc.setDrawColor(...SLATE_200); doc.roundedRect(x, cy, cellW - 10, 54, 4, 4);
      doc.setTextColor(...SLATE_500); doc.setFontSize(8); doc.setFont('helvetica', 'bold');
      doc.text(label.toUpperCase(), x + 12, cy + 16);
      doc.setTextColor(...BRAND_NAVY); doc.setFontSize(16); doc.setFont('helvetica', 'bold');
      doc.text(String(value), x + 12, cy + 40);
    });
    y += 150;
  }

  // Custom note — optional
  if (customNote && customNote.trim()) {
    doc.setDrawColor(...SLATE_200); doc.setFillColor(248, 250, 252);
    const noteLines = doc.splitTextToSize(customNote.trim(), contentW - 20);
    const boxH = Math.max(40, noteLines.length * 14 + 24);
    doc.roundedRect(margin, y, contentW, boxH, 4, 4, 'FD');
    doc.setTextColor(...SLATE_500); doc.setFontSize(8); doc.setFont('helvetica', 'bold');
    doc.text('NOTE FROM YOUR SEERTECH TEAM', margin + 12, y + 16);
    doc.setTextColor(...SLATE_700); doc.setFontSize(10); doc.setFont('helvetica', 'normal');
    doc.text(noteLines, margin + 12, y + 30);
    y += boxH + 16;
  }

  // Component summary list
  doc.setTextColor(...BRAND_NAVY); doc.setFontSize(11); doc.setFont('helvetica', 'bold');
  doc.text(`Components in scope (${filteredComponents.length})`, margin, y);
  y += 14;

  doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(...SLATE_700);
  const maxListY = pageH - 60;
  for (const comp of filteredComponents) {
    if (y > maxListY) {
      doc.setFontSize(9); doc.setTextColor(...SLATE_400); doc.setFont('helvetica', 'italic');
      doc.text(`\u2026 and ${filteredComponents.length - filteredComponents.indexOf(comp)} more`, margin, y);
      break;
    }
    const bullet = comp.required ? '\u25AA' : comp.recommended ? '\u25CA' : '\u00B7';
    doc.text(`${bullet}  ${comp.name}`, margin, y);
    doc.setTextColor(...SLATE_400);
    const meta = `${getGroupLabel(comp.group, 'short')}${comp.role ? `  \u2022  ${ROLE_LABELS[comp.role] || comp.role}` : ''}`;
    doc.text(meta, margin + 260, y);
    doc.setTextColor(...SLATE_700);
    y += 12;
  }

  drawFooter(doc, feedbackUrl);

  // ── Per-component pages ───────────────────────────────────────
  for (const comp of filteredComponents) {
    doc.addPage();
    drawHeader(doc, 'SEERTECH \u2014 COMPONENT FACTSHEET', clientLogoDataUri);
    renderComponentPage(doc, comp, 60);
    drawFooter(doc, feedbackUrl);
  }

  // Filename — uses snake_case API fields (organization_name +
  // estimate_name). Prior to v260.30 this looked up camelCase
  // (estimate.organizationName) which always returned undefined and
  // produced colliding "scope-scope-pack.pdf" files; the bulk-export
  // workflow surfaced the collision because every batch generated
  // identical names. We now suffix with a short id slice so even
  // estimates with the same org+name produce distinct downloads.
  const slug = (s) => String(s || '').replace(/[^a-z0-9]+/gi, '-').toLowerCase().replace(/^-|-$/g, '');
  const orgSlug = slug(estimate.organization_name || estimate.organizationName) || 'scope';
  const estSlug = slug(estimate.estimate_name || estimate.estimateName);
  const idSuffix = estimate.id ? `-${String(estimate.id).slice(-6)}` : '';
  const filename = `${orgSlug}${estSlug ? `-${estSlug}` : ''}${idSuffix}-scope-pack.pdf`;
  return { doc, filename, pageCount: filteredComponents.length + 1 };
}

/**
 * Export a full Scope Pack: cover page + one page per selected component.
 * Saves the PDF to the user's device.
 */
export function exportScopePack(estimate, components, options = {}) {
  const { doc, filename } = buildScopePack(estimate, components, options);
  doc.save(filename);
  return filename;
}
